import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db, products_col_name } from '../firebase';
import '../App.css';
import { nativeShare } from '../modules/modules';
import { Helmet } from 'react-helmet';
import { useAuth } from '../context/AuthContext';
import logo from "../assets/logo.png";
import { useTranslation } from 'react-i18next'; 


export const ViewProduct = ({ productId }) => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeImage, setActiveImage] = useState(0);
    const [selectedSize, setSelectedSize] = useState("");
    const [selectedColor, setSelectedColor] = useState(0);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        const fetchProduct = async () => {
            if (!productId) {
                setError("Product ID not found.");
                setLoading(false);
                return;
            }

            try {
                const docRef = doc(db, products_col_name, productId);
                const docSnap = getDoc(docRef);
                const snapshot = await docSnap;

                if (snapshot.exists()) {
                    const data = snapshot.data();
                    setProduct({ id: snapshot.id, ...data });
                    // Set default size if available
                    if (data.sizes && data.sizes.length > 0) {
                        setSelectedSize(data.sizes[0]);
                    }
                } else {
                    setError("Product not found.");
                }
            } catch (err) {
                console.error("Error fetching product:", err);
                setError("Failed to load product details.");
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [productId]);

    const handleUpdate = () => {
        navigate('/admin/dashboard', { state: { openTab: 'manage-products', editProductId: productId } });
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!productId) return;
        setDeleting(true);
        try {
            await deleteDoc(doc(db, products_col_name, productId));
            setShowDeleteConfirm(false);
            navigate('/products', { replace: true });
        } catch (err) {
            console.error('Error deleting product:', err);
            setError('Failed to delete product.');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    if (loading) {
        return (
            <div className="view-product-container" style={{ justifyContent: 'center', alignItems: 'center' }}>
                <div className="loading-spinner" style={{ color: "#000000" }}>Loading product...</div>
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="view-product-container" style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                <h2>{error || "Product not found"}</h2>
                <button onClick={() => window.history.back()} style={{ padding: '10px 20px', marginTop: '20px', cursor: 'pointer' }}>Go Back</button>
            </div>
        );
    }

    // Handle images safely
    const extraimages = product.extraImages || [];
    const images = [product.coverImage, ...extraimages];
    const displayImage = images[activeImage] || "https://placehold.co/600x600/1a1a1a/FFF?text=No+Image";

    // Handle prices safely
    const price = product.price || 0;
    const originalPrice = product.originalPrice || null;
    const onPromotion = product.onPromotion || false;
    const promotionPrice = product.promotionPrice || null;
    // Handle colors and sizes safely
    const colors = product.colors || [];
    const sizes = product.sizes || [];

    return (
        <div className="view-product-container">
            <Helmet>
                <title>{product.name} - Daty Abaya</title>
                <meta property="og:title" content={product.name} />
                <meta property="og:description" content={product.description || "Check out this product on Daty Abaya!"} />
                <meta property="og:image" content={displayImage} />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:type" content="product" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={product.name} />
                <meta name="twitter:description" content={product.description || "Check out this product on Daty Abaya!"} />
                <meta name="twitter:image" content={displayImage} />
            </Helmet>
            {/* Left Column: Gallery */}
            <div className="product-gallery">
                <div className="main-image-container">
                    <img
                        src={displayImage}
                        alt={product.title}
                        className="main-image"
                    />
                </div>
                {images.length > 1 && (
                    <div className="thumbnail-strip">
                        {images.map((img, index) => (
                            <div
                                key={index}
                                className={`thumbnail ${activeImage === index ? 'active' : ''}`}
                                onClick={() => setActiveImage(index)}
                            >
                                <img
                                    src={img}
                                    alt={`View ${index + 1}`}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Right Column: Details */}
            <div className="product-details">
                <div className="product-breadcrumbs">
                    {product.category}
                </div>

                <h1 className="product-title">{product.name}</h1>

                <div className="product-price">
                    {onPromotion && promotionPrice ? (

                        <span className="promotion-div">
                            <small className='promo-label'>{t("on-offer")}</small>
                            <small className='promo-label price1' style={{ color: "#00000049" }}><s>Tsh {Number(price).toLocaleString()}</s></small>
                            <small className='promo-label pecantage'>{`- ${(((price - promotionPrice) / price) * 100).toFixed(0)}%`}</small>
                            <span className='promo-price'>Tsh {Number(promotionPrice).toLocaleString()}</span>
                        </span>
                    ) : (
                        <span className="original-price">
                            Tsh {Number(price).toLocaleString()}
                        </span>
                    )}
                </div>

                <p className="product-description">
                    {product.description || "No description available."}
                </p>

                <div className="product-options">
                    <div className="option-group">
                        <small className="option-label">{t("size")}: {selectedSize}</small>
                        <div className="size-selector">
                            <p className="product-title" style={{ color: "green" }}>{product.size}</p>
                        </div>
                    </div>

                    <div className="option-group">
                        <small className="option-label">{t("color")}</small>
                        <div className="color-selector">
                            <p className="product-title" style={{ color: "#383838" }}>{product.color}</p>
                        </div>
                    </div>
                </div>

                <div className="product-actions">
                    {currentUser && (
                        <div className="product-actions__admin">
                            <button
                                type="button"
                                className="btn-action btn-action--update"
                                onClick={handleUpdate}
                                aria-label="Update product"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                Update
                            </button>
                            <button
                                type="button"
                                className="btn-action btn-action--delete"
                                onClick={handleDeleteClick}
                                disabled={deleting}
                                aria-label="Delete product"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
                                {deleting ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        className="btn-action btn-action--share"
                        onClick={() => { nativeShare(product.name, product.description, window.location.href); }}
                        aria-label="Share product"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3"></circle>
                            <circle cx="6" cy="12" r="3"></circle>
                            <circle cx="18" cy="19" r="3"></circle>
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                        </svg>
                        Share
                    </button>
                </div>

                {showDeleteConfirm && (
                    <div className="product-delete-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-dialog-title">
                        <div className="product-delete-modal">
                            <h3 id="delete-dialog-title">Delete this product?</h3>
                            <p>This action cannot be undone.</p>
                            <div className="product-delete-modal__actions">
                                <button type="button" className="btn-action btn-action--update" onClick={handleDeleteCancel} disabled={deleting}>
                                    Cancel
                                </button>
                                <button type="button" className="btn-action btn-action--delete" onClick={handleDeleteConfirm} disabled={deleting}>
                                    {deleting ? 'Deleting…' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="product-meta">
                    <div className="meta-item">
                        <span>🚚 Delivery available</span>
                    </div>
                    <div className="meta-item">
                        <span>🛡️ Quality Guarantee</span>
                    </div>
                </div>
            </div>
        </div>
    );
}; 